/**
 * TNE 2026 — © Mika (Mikaël FOURNY) pour la DRANE Nancy-Metz.
 * src/stores/plastronGuard.ts
 *
 * Rôle :
 *   Store Zustand global pour la modale pédagogique « Compte plastron
 *   de démo : cette action est désactivée ». Pilote l'ouverture/
 *   fermeture du <PlastronGuardModal> monté dans <AppLayout>.
 *
 *   API impérative — appelable depuis n'importe quel handler côté
 *   front (de préférence via le hook `usePlastronGuard()`) :
 *     plastronGuardStore.getState().open('S\'inscrire à un atelier');
 *
 * Réutilisable :
 *   100 %. Pattern store global modal (équivalent toast.ts) sans
 *   couplage métier — l'argument `reason` est juste un wording libre.
 *
 * Voir :
 *   - src/components/system/PlastronGuardModal.tsx (rendu)
 *   - src/hooks/usePlastronGuard.ts (consommation typique)
 *   - src/stores/toast.ts (pattern de référence)
 *   - CLAUDE.md §5/§24 2026-05-20 (feature plastron)
 *
 * @author Mika
 * @since  2026-05-20
 */

import { create } from 'zustand';

interface PlastronGuardState {
  /** True si la modale doit être ouverte. */
  isOpen: boolean;
  /**
   * Raison contextuelle affichée dans la modale (libellé d'action
   * que le plastron a tenté — ex. « S'inscrire à un atelier »,
   * « Modifier mon profil »…). Wording FR strict (§15.5).
   * Vide → l'UI affiche un message générique de fallback.
   */
  reason: string;
  /** Ouvre la modale avec une raison contextuelle. */
  open: (reason: string) => void;
  /** Ferme la modale (close manuel ou « Continuer en démo »). */
  close: () => void;
}

export const usePlastronGuardStore = create<PlastronGuardState>((set) => ({
  isOpen: false,
  reason: '',
  open: (reason: string) =>
    set({
      isOpen: true,
      // Trim défensif — le caller peut passer un libellé i18n résolu
      // avec espaces de bordure (formatMessage). Vide accepté.
      reason: typeof reason === 'string' ? reason.trim() : '',
    }),
  close: () => set({ isOpen: false, reason: '' }),
}));
